<template>
  <div class="group-item" :class="{ 'theme-dark': dark }">
    <div
      class="group-header"
      :class="{ active: expanded || hasActiveSegment }"
      @click="$emit('toggle')"
    >
      <div class="group-info">
        <div v-if="group.productImage" class="group-image">
          <img :src="group.productImage" alt="商品图片" />
          <v-chip
            v-if="group.isReturn"
            size="x-small"
            color="orange"
            variant="flat"
            label
            class="return-chip"
          >返场</v-chip>
        </div>

        <div class="group-summary">
          <div class="product-name" v-html="group.productName"></div>
          <div class="group-details">
            <span class="tag-influencer" :title="group.talentNickname">
              <i class="mdi mdi-account-circle-outline"></i>
              {{ group.talentNickname }}
            </span>
            <span v-if="group.liveStartTime" class="tag-date">
              <i class="mdi mdi-calendar-blank-outline"></i>
              {{ group.liveStartTime.split(' ')[0] }}
            </span>
          </div>
          <div class="group-stats">
            <span>共 <b>{{ segments.length }}</b> 个片段</span>
            <span class="stat-badge recorded">{{ recordedSegments }}录制</span>
            <span v-if="notRecordedSegments > 0" class="stat-badge not-recorded">
              {{ notRecordedSegments }}未录
            </span>
            <span><i class="mdi mdi-timer-outline"></i> {{ group.totalDuration }}</span>
          </div>
          <div class="group-sales">
            <span v-if="group.estimatedGmvStr && group.estimatedGmvStr !== '-'" class="gmv">
              <i class="mdi mdi-currency-cny"></i> {{ group.estimatedGmvStr }}
            </span>
            <span v-if="group.efficiency && group.efficiency !== '-'" class="efficiency">
              <i class="mdi mdi-lightning-bolt"></i> 效率 {{ formatNumber(group.efficiency, '/分') }}
            </span>
          </div>
        </div>
      </div>
      <div class="expand-icon" :class="{ expanded }">▶</div>
    </div>

    <div v-if="expanded" class="group-content">
      <div
        v-for="(segment, index) in segments"
        :key="segment['录制ID'] || `${segment['讲解开始时间']}_${index}`"
        class="segment-item result-item"
        :class="{
          active: selectedSegment === segment,
          disabled: !segment['已录制'],
          'active-playing': activeSegment === segment
        }"
        @click.stop="$emit('select', segment, group)"
      >
        <div class="segment-heading">
          <span class="segment-index">第{{ segment._dailyIndex || index + 1 }}段</span>
          <span class="segment-time">开始时间 {{ formatTime(segment['讲解开始时间']) }}</span>
        </div>
        <div class="segment-stats">
          <div>
            <span>讲解时长</span>
            <strong class="duration">{{ formatDuration(segment['讲解时长（分钟）']) }}分钟</strong>
          </div>
          <div>
            <span>全网销量</span>
            <strong>{{ formatSales(segment['全网销量']) }}</strong>
          </div>
          <div class="recording-state">
            <span>录制状态</span>
            <strong :class="segment['已录制'] ? 'tag-recorded' : 'tag-unrecorded'">
              {{ segment['已录制'] ? '已录制' : '未录制' }}
            </strong>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  group: {
    type: Object,
    required: true
  },
  expanded: Boolean,
  selectedSegment: {
    type: Object,
    default: null
  },
  activeSegment: {
    type: Object,
    default: null
  },
  dark: Boolean
})

defineEmits(['toggle', 'select'])

const segments = computed(() => props.group.segments || [])
const recordedSegments = computed(() => props.group.recordedSegments ?? segments.value.filter(item => item['已录制']).length)
const notRecordedSegments = computed(() => props.group.notRecordedSegments ?? (segments.value.length - recordedSegments.value))
const hasActiveSegment = computed(() => props.activeSegment && segments.value.includes(props.activeSegment))

const formatTime = (time) => time || ''

const formatDuration = (duration) => {
  const value = Number(duration)
  return Number.isFinite(value) ? value.toFixed(1) : '0.0'
}

const formatSales = (sales) => {
  return sales && !['0', 0, '0.0'].includes(sales) ? sales : '未知'
}

const formatNumber = (num, unit = '') => {
  const value = Number(num)
  if (!Number.isFinite(value)) return num || ''
  if (value >= 100000000) return `${parseFloat((value / 100000000).toFixed(2))}亿${unit}`
  if (value >= 10000) return `${parseFloat((value / 10000).toFixed(2))}W${unit}`
  if (value >= 1000) return `${parseFloat((value / 1000).toFixed(2))}K${unit}`
  return `${parseFloat(value.toFixed(2))}${unit}`
}
</script>

<style scoped>
.group-item {
  --group-bg: #fff;
  --header-bg: #fcfcfc;
  --header-hover-bg: #f5f7fa;
  --header-active-bg: #f0f7ff;
  --content-bg: #f9fbfd;
  --segment-bg: #fff;
  --segment-disabled-bg: #fafafa;
  --primary-text: #333;
  --secondary-text: #666;
  --muted-text: #888;
  --border: #e0e0e0;
  --segment-border: #eef2f6;
  margin: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--group-bg);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.group-item.theme-dark {
  --group-bg: #1e1e1e;
  --header-bg: #242424;
  --header-hover-bg: #2a2a2a;
  --header-active-bg: #263238;
  --content-bg: #191919;
  --segment-bg: #242424;
  --segment-disabled-bg: #202020;
  --primary-text: #eee;
  --secondary-text: #bbb;
  --muted-text: #888;
  --border: #3a3a3a;
  --segment-border: #333;
  margin: 8px 0;
  box-shadow: none;
}

.group-header {
  padding: 12px;
  background: var(--header-bg);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;
}

.group-header:hover { background: var(--header-hover-bg); }
.group-header.active {
  background: var(--header-active-bg);
  border-bottom: 1px solid var(--border);
}

.group-info { display: flex; gap: 10px; align-items: flex-start; flex: 1; min-width: 0; }
.group-image { flex-shrink: 0; display: flex; flex-direction: column; align-items: center; gap: 4px; }
.group-image img { width: 60px; height: 60px; object-fit: cover; border-radius: 4px; }
.return-chip { height: 18px !important; font-size: 10px !important; padding: 0 4px !important; }
.group-summary { flex: 1; min-width: 0; }
.product-name { color: var(--primary-text); font-weight: bold; word-break: break-all; line-height: 1.4; }
.group-details { margin: 4px 0 6px; display: flex; align-items: center; gap: 15px; flex-wrap: wrap; }
.tag-influencer { background: #e3f2fd; color: #1565c0; padding: 2px 6px; border-radius: 4px; font-size: 0.85em; display: flex; align-items: center; gap: 4px; }
.tag-date { color: var(--muted-text); font-size: 0.85em; display: flex; align-items: center; gap: 4px; }
.group-stats { display: flex; gap: 8px; font-size: 0.85em; color: var(--secondary-text); align-items: center; flex-wrap: wrap; }
.stat-badge { padding: 1px 6px; border-radius: 4px; font-size: 0.8em; font-weight: 500; }
.recorded, .tag-recorded { background: #e8f5e9; color: #2e7d32; border: 1px solid #c8e6c9; }
.not-recorded, .tag-unrecorded { background: #fff3e0; color: #f57c00; border: 1px solid #ffe0b2; }
.group-sales { margin-top: 6px; display: flex; gap: 15px; font-size: 0.9em; flex-wrap: wrap; }
.gmv { color: #d32f2f; font-weight: bold; }
.efficiency { color: #ef6c00; font-weight: bold; }
.expand-icon { font-size: 0.9em; color: #bbb; transition: transform 0.3s; margin-left: 10px; }
.expand-icon.expanded { transform: rotate(90deg); color: #1976d2; }

.group-content { background: var(--content-bg); padding: 10px; display: flex; flex-direction: column; gap: 8px; }
.segment-item { background: var(--segment-bg); color: var(--secondary-text); border: 1px solid var(--segment-border); border-radius: 6px; padding: 12px; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s; }
.segment-item:hover { transform: translateY(-1px); box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08); border-color: #d1d9e6; }
.segment-item.active { border: 1.5px solid #1976d2; background: var(--header-active-bg); }
.segment-item.active-playing { border: 1.5px solid #e53935; background: #ffebee; }
.theme-dark .segment-item.active-playing { background: #3a2426; }
.segment-item.disabled { opacity: 0.7; cursor: not-allowed; background: var(--segment-disabled-bg); }
.theme-dark .segment-item.disabled { opacity: 1; }
.segment-heading { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.segment-index { color: #1976d2; font-weight: bold; font-size: 0.9em; }
.segment-time { font-size: 0.85em; color: var(--secondary-text); }
.segment-stats { display: flex; margin-top: 8px; font-size: 0.85em; color: var(--secondary-text); }
.segment-stats > div { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.segment-stats strong { color: var(--primary-text); font-weight: normal; }
.segment-stats .duration { color: #2e7d32; font-weight: bold; }
.segment-stats .recording-state { text-align: right; align-items: flex-end; }
.segment-stats .tag-recorded, .segment-stats .tag-unrecorded { display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: normal; }
.theme-dark .segment-stats .tag-recorded {
  background: #173d24;
  color: #8ee6a8;
  border-color: #327a49;
  font-weight: 600;
}
.theme-dark .segment-stats .tag-unrecorded {
  background: #4b3108;
  color: #ffd180;
  border-color: #9a681d;
  font-weight: 600;
}
</style>
